import { FormEvent, useEffect, useState } from "react";
import {
  createCategory,
  deleteCategory,
  getCategories,
  updateCategory,
} from "../service/CatalogService";
import { Category } from "../model/types";
import { Alert } from "../util/Alert";
import { Loading } from "../util/Loading";
import { extractErrorMessage } from "../service/api";

const emptyForm = { name: "", description: "" };

export const CategoriesPage = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const loadCategories = async () => {
    setLoading(true);
    try {
      setCategories(await getCategories());
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    try {
      if (editingId) {
        await updateCategory(editingId, form);
        setMessage("Category updated");
      } else {
        await createCategory(form);
        setMessage("Category created");
      }
      setForm(emptyForm);
      setEditingId(null);
      await loadCategories();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const beginEdit = (category: Category) => {
    setEditingId(category.id);
    setForm({ name: category.name, description: category.description });
  };

  const handleDelete = async (categoryId: number) => {
    try {
      await deleteCategory(categoryId);
      setMessage("Category deleted");
      await loadCategories();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  if (loading) {
    return <Loading label="Loading categories..." />;
  }

  return (
    <section className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
      <form
        onSubmit={handleSubmit}
        className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm"
      >
        <p className="text-xs uppercase tracking-[0.24em] text-emerald-700">
          Category Management
        </p>
        <h2 className="mt-2 text-2xl font-semibold text-slate-900">
          {editingId ? "Edit category" : "Create category"}
        </h2>
        <div className="mt-6 space-y-4">
          {message ? (
            <Alert type="success" message={message} onClose={() => setMessage("")} />
          ) : null}
          {error ? (
            <Alert type="failed" message={error} onClose={() => setError("")} />
          ) : null}
          <input
            value={form.name}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, name: event.target.value }))
            }
            placeholder="Category name"
            className="w-full rounded-2xl border border-stone-300 px-4 py-3"
            required
          />
          <textarea
            value={form.description}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, description: event.target.value }))
            }
            rows={4}
            placeholder="Description"
            className="w-full rounded-2xl border border-stone-300 px-4 py-3"
          />
          <div className="flex gap-3">
            <button
              type="submit"
              className="rounded-2xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white"
            >
              {editingId ? "Update" : "Create"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={() => {
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="rounded-2xl border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-700"
              >
                Cancel
              </button>
            ) : null}
          </div>
        </div>
      </form>

      <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm">
        <h3 className="text-xl font-semibold text-slate-900">Existing categories</h3>
        <div className="mt-5 space-y-4">
          {categories.map((category) => (
            <div
              key={category.id}
              className="flex flex-col gap-4 rounded-2xl border border-stone-200 p-4 lg:flex-row lg:items-center lg:justify-between"
            >
              <div>
                <h4 className="text-lg font-semibold text-slate-900">
                  {category.name}
                </h4>
                <p className="mt-1 text-sm text-stone-600">
                  {category.description}
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => beginEdit(category)}
                  className="rounded-2xl border border-stone-300 px-4 py-2 text-sm font-semibold text-stone-700"
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(category.id)}
                  className="rounded-2xl border border-rose-200 px-4 py-2 text-sm font-semibold text-rose-700"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
